import React, { useEffect } from 'react';
import {CategorySelector} from './CategorySelector';
import {EditorContent, ReactNodeViewRenderer, useEditor, Editor} from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import TextAlign from '@tiptap/extension-text-align';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import Subscript from '@tiptap/extension-subscript';
import Superscript from '@tiptap/extension-superscript';
import TextStyle from '@tiptap/extension-text-style';
import {Mark} from '@tiptap/core';
import Color from '@tiptap/extension-color';
import Highlight from '@tiptap/extension-highlight';
import FontFamily from '@tiptap/extension-font-family';
import BulletList from '@tiptap/extension-bullet-list';
import OrderedList from '@tiptap/extension-ordered-list';
import ListItem from '@tiptap/extension-list-item';
import Image from '@tiptap/extension-image';
import HardBreak from '@tiptap/extension-hard-break';
import Table from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableHeader from '@tiptap/extension-table-header';
import TableCell from '@tiptap/extension-table-cell';
import {ResizableImageComponent} from './ResizableImage';
import './NoteForm.css';
import './Toolbar.css';
import {Toolbar} from './Toolbar';
import {Note} from '../types/note';

const FontSize = Mark.create({
    name: 'fontSize',
    addOptions() {
        return {types: ['textStyle']};
    },
    addAttributes() {
        return {
            size: {
                default: null,
                parseHTML: element => element.style.fontSize || null,
                renderHTML: attributes => {
                    if (!attributes.size) return {};
                    return {style: `font-size: ${attributes.size}`};
                },
            },
        };
    },
    parseHTML() {
        return [{style: 'font-size', getAttrs: value => ({size: value})}];
    },
    renderHTML({HTMLAttributes}) {
        return ['span', HTMLAttributes, 0];
    },
});

interface NoteEditorProps {
    selectedCategoryId: number,
    onCategoryChange: (categoryId: number) => void,
    isSaving: boolean,
    onSave: (content: string) => void,
    onAddImage?: (file: File) => Promise<string | null>,
    onAddFile?: (file: File) => void,
    onSetLink?: () => void,
    onEditorReady?: (editor: Editor) => void,
    showSaveButton?: boolean,
    onCancel?: () => void,
    uploadProgress?: { [key: string]: number },
    note?: Note,
    categoryBreadcrumb?: string,
    editMode?: boolean
}

export const NoteEditor: React.FC<NoteEditorProps> = ({
                                                          isSaving,
                                                          onSave,
                                                          onAddImage,
                                                          onAddFile,
                                                          onSetLink,
                                                          onEditorReady,
                                                          showSaveButton = true,
                                                          onCancel,
                                                          uploadProgress,
                                                          selectedCategoryId,
                                                          onCategoryChange,
                                                          note,
                                                          categoryBreadcrumb,
                                                          editMode = true
                                                      }) => {
    const editor: Editor | null = useEditor({
        extensions: [
            StarterKit.configure({bulletList: false, orderedList: false, listItem: false}),
            BulletList.configure({keepMarks: true, keepAttributes: false}),
            OrderedList.configure({keepMarks: true, keepAttributes: false}),
            ListItem,
            TextAlign.configure({types: ['heading', 'paragraph', 'bulletList', 'orderedList', 'table', 'tableCell', 'tableHeader']}),
            Underline,
            Table.configure({
                resizable: true,
                HTMLAttributes: {
                    class: 'note-table',
                },
            }),
            TableRow,
            TableHeader,
            TableCell,
            Link.configure({
                openOnClick: false,
                HTMLAttributes: {class: 'note-link'},
                validate: href => /^https?:\/\//.test(href),
            }),
            Subscript,
            Superscript,
            TextStyle,
            FontSize,
            Color,
            Highlight.configure({multicolor: true}),
            FontFamily,
            Image.configure({
                HTMLAttributes: {class: 'note-image'},
                allowBase64: true,
                inline: false,
            }).extend({
                addNodeView() {
                    return ReactNodeViewRenderer(ResizableImageComponent);
                },
            }),
            HardBreak,
        ],
        content: note?.content || '',
        editorProps: {
            attributes: {
                class: 'max-w-none',
            },
        },
        autofocus: false,
        injectCSS: false,
        onCreate: ({ editor }) => {
            // Remove any focus-related classes that might be added by tiptap
            editor.view.dom.classList.add('outline-none', 'focus:outline-none', 'focus-visible:outline-none');
            if (onEditorReady) {
                onEditorReady(editor);
            }
        },
        editable: editMode
    });

    const handleSave = () => {
        if (editor) {
            onSave(editor.getHTML());
        }
    };

    const handleImageUpload = async (file: File) => {
        if (!onAddImage || !editor) return;
        const imageUrl = await onAddImage(file);
        if (imageUrl !== null) {
            try {
                editor.chain().focus().setImage({src: imageUrl}).run();
            } catch (error) {
                console.error('Error inserting image:', error);
            }
        }
    };

    return (
        <div className="flex flex-col h-full bg-white dark:bg-gray-900">
            {/* Header with actions - Improved styling */}
            <div className="sticky top-0 z-20 bg-white dark:bg-gray-800 shadow-sm">
                {(editMode || showSaveButton || onCancel) && (
                    <>
                        <div className="max-w-5xl mx-auto w-full px-6 py-4 flex justify-between items-center">
                            {editMode && (
                                <div className="flex-1">
                                    <CategorySelector
                                        selectedCategoryId={selectedCategoryId}
                                        categoryBreadcrumb={categoryBreadcrumb}
                                        onCategoryChange={onCategoryChange}
                                        note={note}
                                    />
                                </div>
                            )}
                            {(showSaveButton || onCancel) && (
                                <div className="flex items-center gap-3 ml-4">
                                    {onCancel && (
                                        <button
                                            onClick={onCancel}
                                            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
                                            type="button"
                                        >
                                            Cancel
                                        </button>
                                    )}
                                    {showSaveButton && (
                                        <button
                                            onClick={handleSave}
                                            className="px-4 py-2 text-sm font-medium bg-[#2382e2] hover:bg-[#1a6fc7] text-white rounded-md transition-colors flex items-center shadow-sm"
                                            disabled={isSaving}
                                        >
                                            {isSaving ? (
                                                <>
                                                    <svg className="animate-spin h-4 w-4 mr-2" viewBox="0 0 24 24">
                                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                                                        <path
                                                            className="opacity-75"
                                                            fill="currentColor"
                                                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                                        />
                                                    </svg>
                                                    Saving...
                                                </>
                                            ) : (
                                                'Save'
                                            )}
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>

                        {editMode && (
                            <div className="bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700">
                                <div className="max-w-5xl mx-auto px-6 py-1">
                                    <Toolbar
                                        editor={editor}
                                        onAddImage={handleImageUpload}
                                        onAddFile={onAddFile}
                                        onSetLink={onSetLink}
                                        uploadProgress={uploadProgress}
                                    />
                                </div>
                            </div>
                        )}
                    </>
                )}

            </div>

            {/* Content area - Improved styling */}
            <div className="flex-1 overflow-y-auto w-full bg-white dark:bg-gray-900">
                <div className="max-w-5xl mx-auto w-full py-8 px-6">
                    <div className="notion-content">
                        <EditorContent 
                            editor={editor} 
                            className="min-h-[calc(100vh-250px)] w-full outline-none focus:outline-none"
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};